import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Coins,
  PauseCircle,
  Plus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  INDUSTRY_OPTIONS,
  optionLabel,
  type ProgramStatus,
} from "./programs/mock-programs";
import { ProgramsApi, programKeys, type ProgramView } from "@/api/perkpoint/programs";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { ProgramRowActions } from "./programs/ProgramRowActions";

/** "1.2M" / "420K" / "12,400" → number. */
function parseCompact(value: string): number {
  const match = value.trim().replace(/,/g, "").match(/^([\d.]+)\s*([KMB])?$/i);
  if (!match) return 0;
  const scale = { K: 1e3, M: 1e6, B: 1e9 }[
    (match[2] ?? "").toUpperCase() as "K" | "M" | "B"
  ];
  return Number(match[1]) * (scale ?? 1);
}

function formatCompact(value: number): string {
  if (value >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return value.toLocaleString("en-IN");
}

const STATUS_STYLES: Record<ProgramStatus, string> = {
  Active: "bg-green-50 text-green-700",
  Draft: "bg-slate-100 text-slate-600",
  Paused: "bg-amber-50 text-amber-700",
  Closed: "bg-red-50 text-red-700",
};

function StatusBadge({ status }: { status: ProgramStatus }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        STATUS_STYLES[status],
      )}
    >
      {status}
    </span>
  );
}

function Readiness({ program }: { program: ProgramView }) {
  if (program.status !== "Draft") {
    return <span className="text-sm text-slate-500">Launched</span>;
  }
  const gaps = program.launchGaps;
  if (gaps.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-green-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Ready to launch
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-sm text-orange-600"
      title={gaps.join(", ")}
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      {gaps.length} item{gaps.length === 1 ? "" : "s"} missing
    </span>
  );
}

/** Loyyal admin home: every Perk Point program at a glance. */
export default function PortfolioDashboard() {
  const programsQuery = useQuery({
    queryKey: programKeys.list({ pageSize: 100 }),
    queryFn: () => ProgramsApi.list({ pageSize: 100 }),
  });
  const programs = useMemo(() => programsQuery.data?.items ?? [], [programsQuery.data]);
  const apiStats = programsQuery.data?.stats;

  const stats = useMemo(() => {
    const count = (status: ProgramStatus) =>
      programs.filter((program) => program.status === status).length;
    return {
      total: apiStats?.total ?? programs.length,
      active: apiStats?.active ?? count("Active"),
      draft: apiStats?.draft ?? count("Draft"),
      paused: apiStats?.paused ?? count("Paused"),
      members: apiStats?.linked_members ?? programs.reduce((sum, p) => sum + p.membersCount, 0),
      points: programs.reduce((sum, p) => sum + parseCompact(p.pointsIssued), 0),
    };
  }, [programs, apiStats]);

  const attention = useMemo(
    () =>
      programs.flatMap((program) => {
        if (program.status === "Paused") {
          return [
            {
              program,
              title: `${program.name} is paused`,
              detail: "Members cannot earn or redeem until it is resumed.",
            },
          ];
        }
        const gaps =
          program.status === "Draft" ? program.launchGaps : [];
        if (program.status === "Draft") {
          return [
            {
              program,
              title: `${program.name} is still in draft`,
              detail: gaps.length
                ? `Needed before launch: ${gaps.join(", ")}.`
                : "Setup is complete. It can be launched.",
            },
          ];
        }
        return [];
      }),
    [programs],
  );

  const byMembers = useMemo(
    () => [...programs].sort((a, b) => b.membersCount - a.membersCount),
    [programs],
  );
  const maxMembers = Math.max(1, ...byMembers.map((p) => p.membersCount));

  const kpis = [
    {
      label: "Programs",
      value: stats.total,
      meta: `${stats.active} active`,
      icon: Building2,
    },
    {
      label: "Drafts awaiting launch",
      value: stats.draft,
      meta: "Finish setup to go live",
      icon: AlertTriangle,
    },
    {
      label: "Paused",
      value: stats.paused,
      meta: "Earn and redeem stopped",
      icon: PauseCircle,
    },
    {
      label: "Linked members",
      value: stats.members.toLocaleString("en-IN"),
      meta: "Across all programs",
      icon: Users,
    },
    {
      label: "Points issued",
      value: formatCompact(stats.points),
      meta: "Lifetime, all programs",
      icon: Coins,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              All programs
            </h1>
          </div>
          <p className="mt-1 max-w-2xl text-muted-foreground">
            Every Perk Point program you run. Log in as manager on a program to
            work inside it.
          </p>
        </div>
        <Button asChild className="bg-green-600 text-white hover:bg-green-700">
          <Link to="/perkpoint/program/setup/create">
            <Plus className="mr-2 h-4 w-4" /> Create program
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{kpi.label}</p>
                <kpi.icon className="h-4 w-4 text-green-700" />
              </div>
              <p className="mt-2 text-3xl font-bold text-slate-900">
                {kpi.value}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{kpi.meta}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardContent className="space-y-4 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">
                Programs
              </h2>
              <Button asChild variant="outline" size="sm" className="bg-white">
                <Link to="/perkpoint/program/setup">View all</Link>
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead>Program</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead className="text-right">Members</TableHead>
                    <TableHead className="text-right">Points issued</TableHead>
                    <TableHead>Readiness</TableHead>
                    <TableHead className="w-12 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-10 text-center text-sm text-muted-foreground"
                      >
                        {programsQuery.isLoading
                          ? "Loading programs…"
                          : programsQuery.isError
                            ? apiErrorMessage(programsQuery.error, "Could not load programs")
                            : "No programs yet. Create the first one."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    programs.map((program) => (
                      <TableRow key={program.id}>
                        <TableCell>
                          <Link
                            to={`/perkpoint/program/setup/${program.id}`}
                            className="font-medium text-slate-900 hover:underline"
                          >
                            {program.name}
                          </Link>
                          <p className="text-xs text-muted-foreground">
                            {program.programCode} · {program.currency}
                          </p>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={program.status} />
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {optionLabel(INDUSTRY_OPTIONS, program.industry)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-700">
                          {program.membersCount.toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-700">
                          {program.pointsIssued}
                        </TableCell>
                        <TableCell>
                          <Readiness program={program} />
                        </TableCell>
                        <TableCell className="text-right">
                          <ProgramRowActions program={program} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-3 p-5">
              <h2 className="text-base font-semibold text-slate-900">
                Needs attention
              </h2>
              {attention.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Every program is live. Nothing waiting on you.
                </p>
              ) : (
                <ul className="space-y-3">
                  {attention.map(({ program, title, detail }) => (
                    <li key={program.id}>
                      <Link
                        to={`/perkpoint/program/setup/${program.id}`}
                        className="block rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
                      >
                        <p className="text-sm font-medium text-slate-900">
                          {title}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {detail}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-3 p-5">
              <h2 className="text-base font-semibold text-slate-900">
                Members by program
              </h2>
              <ul className="space-y-3">
                {byMembers.map((program) => (
                  <li key={program.id}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate text-slate-700">
                        {program.name}
                      </span>
                      <span className="tabular-nums text-slate-900">
                        {program.membersCount.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-100">
                      <div
                        className="h-1.5 rounded-full bg-green-600"
                        style={{
                          width: `${(program.membersCount / maxMembers) * 100}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
